/**
 * POST AN OFFER (FR-U-TXN-001, FR-U-TXN-012). The seller's side of the marketplace.
 *
 * ## The one thing this screen exists to get right
 *
 * The payment methods are not a preference. They decide the release path for every trade on the
 * offer, and `txn.release_path_for_offer` requires **all** of them to qualify: adding one method the
 * platform cannot observe drags the whole offer onto the manual path, where the seller has to confirm
 * every receipt by hand.
 *
 * The engine's own docstring asks for this explicitly: a seller "should learn that while choosing
 * rather than afterwards". So the consequence is rendered live, under the checkboxes, and it names
 * which method caused it. A summary shown after posting would be a report; this is a decision aid.
 *
 * ## Amounts are decimal, in the asset's own units
 *
 * "0.5" BTC, not "50000000". `parse_money` converts server-side and rejects a third decimal place on
 * a two-decimal asset rather than truncating it, and that remains the authoritative check: what goes
 * on the wire is the decimal string the seller typed, at the scale the server holds.
 *
 * The form now checks the same rule BEFORE submitting, through `decimalsFor` in lib/money.ts. That
 * table is a duplicate of the backend's and its docstring says so at length, which is a real cost;
 * the alternative was a seller who types nine decimal places into a six-decimal asset, presses Post,
 * and gets one unlabelled banner naming none of the five number fields on this form. The scale is
 * used to VALIDATE and never to convert an amount that is sent.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LipaApiError, api, type P2POffer, type P2PPaymentMethods } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { decimalsFor, parseMoneyAmount, sanitizeAmountTyping } from '@/lib/money';
import { useResource } from '@/lib/use-resource';

const ASSETS = ['USDT', 'BTC', 'ETH'];

/** Every field the seller can get wrong, keyed so the message renders beside the field itself. */
type FieldErrors = Partial<Record<'price' | 'total' | 'minimum' | 'maximum' | 'methods', string>>;

export default function P2pOfferNew() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useResource<P2PPaymentMethods>(
    () => api.p2pPaymentMethods(),
    [],
  );

  const [side, setSide] = useState<'buy' | 'sell'>('sell');
  const [asset, setAsset] = useState('USDT');
  const [price, setPrice] = useState('');
  const [total, setTotal] = useState('');
  const [minimum, setMinimum] = useState('');
  const [maximum, setMaximum] = useState('');
  const [methods, setMethods] = useState<string[]>([]);
  const [terms, setTerms] = useState('');
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [posted, setPosted] = useState<P2POffer | null>(null);

  const catalogue = useMemo(() => data?.methods ?? [], [data]);

  /**
   * VALIDATION, HERE, BEFORE THE REQUEST.
   *
   * Not because the server cannot be trusted to refuse -- it does, `parse_money` and the engine both
   * do -- but because a 422 arriving after a submit becomes ONE banner at the bottom of a form with
   * five number fields in it, and the seller has to guess which one it meant. The rules below are the
   * server's own, restated where the person typing can see them:
   *
   *   - amounts carry at most the asset's decimal places, and are NEVER truncated to fit
   *   - the price is in KES and has two
   *   - minimum <= maximum <= total, which is the ordering the engine enforces and the one that is
   *     easiest to get subtly wrong: a maximum above the total advertises more than is being offered
   *   - at least one payment method, because the method decides the release path
   */
  const errors = useMemo<FieldErrors>(() => {
    const found: FieldErrors = {};
    const scale = decimalsFor(asset);

    const priceParsed = parseMoneyAmount(price, 2);
    if (!price.trim()) found.price = 'Enter your price.';
    else if (!priceParsed.ok) found.price = priceParsed.reason;
    else if (priceParsed.minor <= 0n) found.price = 'Your price must be above zero.';

    const totalParsed = parseMoneyAmount(total, scale);
    if (!total.trim()) found.total = 'Enter how much you are offering.';
    else if (!totalParsed.ok) found.total = totalParsed.reason;
    else if (totalParsed.minor <= 0n) found.total = 'The total must be above zero.';

    const minParsed = parseMoneyAmount(minimum, scale);
    if (!minimum.trim()) found.minimum = 'Enter a smallest trade.';
    else if (!minParsed.ok) found.minimum = minParsed.reason;
    else if (minParsed.minor <= 0n) found.minimum = 'The smallest trade must be above zero.';

    const maxParsed = parseMoneyAmount(maximum, scale);
    if (!maximum.trim()) found.maximum = 'Enter a largest trade.';
    else if (!maxParsed.ok) found.maximum = maxParsed.reason;
    else if (maxParsed.minor <= 0n) found.maximum = 'The largest trade must be above zero.';

    if (minParsed.ok && maxParsed.ok && !found.minimum && !found.maximum) {
      if (minParsed.minor > maxParsed.minor) {
        found.maximum = 'The largest trade cannot be smaller than the smallest trade.';
      } else if (totalParsed.ok && !found.total && maxParsed.minor > totalParsed.minor) {
        found.maximum = `The largest trade cannot be more than the ${total} ${asset} you are offering.`;
      }
    }

    if (methods.length === 0) found.methods = 'Choose at least one way you will be paid.';

    return found;
  }, [asset, price, total, minimum, maximum, methods]);

  const complete = Object.keys(errors).length === 0;

  /**
   * The live consequence. ALL selected methods must settle automatically, which is why this is an
   * `every` and not a `some`, and why the manual ones are named: "one of your methods" would leave
   * the seller unticking things at random to find it.
   */
  const consequence = useMemo(() => {
    if (methods.length === 0) return null;
    const chosen = catalogue.filter((m) => methods.includes(m.code));
    const manual = chosen.filter((m) => !m.settles_automatically);
    if (manual.length === 0) {
      return {
        automatic: true,
        text: 'Trades on this offer release as soon as we see the payment arrive. You will not be asked to confirm.',
      };
    }
    return {
      automatic: false,
      text:
        `You will confirm each payment yourself before the asset is released, because ` +
        `${manual.map((m) => m.label.toLowerCase()).join(' and ')} ` +
        `${manual.length === 1 ? 'is a payment we cannot see' : 'are payments we cannot see'}. ` +
        `That applies to the whole offer, including trades paid another way.`,
    };
  }, [methods, catalogue]);

  const toggle = useCallback((code: string) => {
    setMethods((m) => (m.includes(code) ? m.filter((c) => c !== code) : [...m, code]));
  }, []);

  const post = useCallback(async () => {
    setTouched(true);
    if (!complete || pending) return;
    setPending(true);
    setRefusal(null);
    try {
      const offer = await api.createP2POffer({
        side,
        asset,
        quote_asset: 'KES',
        price,
        total,
        minimum,
        maximum,
        methods,
        terms: terms.trim() || undefined,
      });

      /**
       * THE RESPONSE IS CHECKED, not assumed.
       *
       * This used to be `await api.createP2POffer(...)` followed by `navigate('/p2p')`: the return
       * value was discarded, the seller was thrown back to a book that did not contain what they had
       * just posted, and the only evidence anything had happened was that nothing had visibly
       * failed. An offer with no id is a failure the old code could not see.
       */
      if (!offer?.id) throw new Error('The offer service did not return an offer.');
      setPosted(offer);
    } catch (e) {
      // A rejected amount is the usual case here and it is INFORMATIVE: parse_money says exactly
      // which figure had too many decimal places for the asset. Surfacing our own wording instead
      // would replace an actionable message with a generic one.
      setRefusal(
        e instanceof LipaApiError
          ? e.detail.message
          : 'We could not post that offer just now. Nothing has been listed and nothing is locked.',
      );
    } finally {
      setPending(false);
    }
  }, [complete, pending, side, asset, price, total, minimum, maximum, methods, terms]);

  if (error) return <ErrorState body="We could not load the payment methods." onRetry={reload} />;

  /**
   * THE SUCCESS STATE. It exists because posting an offer is a commitment, not a navigation.
   *
   * It says what was created and gives its id, so a seller with a question has the same reference an
   * officer would ask for. It does NOT say the asset is locked, because it is not: escrow holds when
   * somebody takes the offer, and claiming otherwise here would misdescribe where the seller's money
   * is.
   */
  if (posted) {
    return (
      <div className="mx-auto max-w-lg py-10 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m5 13 4 4L19 7" />
          </svg>
        </span>
        <h1 className="mt-4 font-semibold text-ink">Offer posted successfully</h1>
        <p className="mx-auto mt-1 max-w-sm text-sm text-ink-muted">
          Your P2P offer is now active and visible to other participants.
        </p>

        <dl className="mx-auto mt-5 max-w-sm space-y-2 rounded-card border border-border bg-surface p-4 text-left text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-muted">Offer ID</dt>
            <dd className="truncate font-mono text-xs font-medium text-ink">{posted.id}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-muted">Status</dt>
            <dd className="font-medium text-ink">Active</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-muted">{posted.side === 'sell' ? 'Selling' : 'Buying'}</dt>
            <dd className="tabular font-medium text-ink">
              {posted.remaining} {posted.asset}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-muted">Price</dt>
            <dd className="tabular font-medium text-ink">
              {posted.price} {posted.quote_asset} per {posted.asset}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-muted">Settlement</dt>
            <dd className="font-medium text-ink">
              {posted.settles_automatically ? 'Automatic' : 'You confirm each payment'}
            </dd>
          </div>
        </dl>

        <p className="mx-auto mt-3 max-w-sm rounded-card bg-surface-sunken p-3 text-xs text-ink-muted">
          Nothing is locked yet. Your {posted.asset} goes into escrow when somebody takes the offer,
          and not before.
        </p>

        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => navigate(`/p2p?side=${posted.side === 'sell' ? 'buy' : 'sell'}`)}
            className="rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light"
          >
            View offer
          </button>
          <Link
            to="/p2p"
            className="rounded-field border border-border px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Back to P2P
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <header>
        <Link to="/p2p" className="text-sm text-brand hover:underline">
          &larr; Back to offers
        </Link>
        <h1 className="mt-2 text-page-title text-ink">Post an offer</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Set your price and how you will be paid. Your asset is locked in escrow only when somebody
          takes the offer, never before.
        </p>
      </header>

      <div className="space-y-4 rounded-card border border-border bg-surface p-4 shadow-card">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="offer-side" className="text-sm font-medium text-ink">
              I want to
            </label>
            <select
              id="offer-side"
              value={side}
              onChange={(e) => setSide(e.target.value as 'buy' | 'sell')}
              className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2 text-sm text-ink"
            >
              <option value="sell">Sell</option>
              <option value="buy">Buy</option>
            </select>
          </div>
          <div>
            <label htmlFor="offer-asset" className="text-sm font-medium text-ink">
              Asset
            </label>
            <select
              id="offer-asset"
              value={asset}
              onChange={(e) => setAsset(e.target.value)}
              className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2 text-sm text-ink"
            >
              {ASSETS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="offer-price" className="text-sm font-medium text-ink">
            Your price
          </label>
          <div className="mt-1 flex items-baseline gap-2">
            <input
              id="offer-price"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(sanitizeAmountTyping(e.target.value))}
              onBlur={() => setTouched(true)}
              aria-invalid={touched && !!errors.price}
              placeholder="0.00"
              className="tabular w-full rounded-field border border-border bg-surface px-3 py-2 text-ink outline-none"
            />
            <span className="whitespace-nowrap text-sm text-ink-muted">KES per {asset}</span>
          </div>
          <FieldError show={touched} message={errors.price} />
        </div>

        <div>
          <label htmlFor="offer-total" className="text-sm font-medium text-ink">
            Total {side === 'sell' ? 'to sell' : 'to buy'}
          </label>
          <div className="mt-1 flex items-baseline gap-2">
            <input
              id="offer-total"
              inputMode="decimal"
              value={total}
              onChange={(e) => setTotal(sanitizeAmountTyping(e.target.value))}
              onBlur={() => setTouched(true)}
              aria-invalid={touched && !!errors.total}
              placeholder="0"
              className="tabular w-full rounded-field border border-border bg-surface px-3 py-2 text-ink outline-none"
            />
            <span className="text-sm text-ink-muted">{asset}</span>
          </div>
          <FieldError show={touched} message={errors.total} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="offer-min" className="text-sm font-medium text-ink">
              Smallest trade
            </label>
            <input
              id="offer-min"
              inputMode="decimal"
              value={minimum}
              onChange={(e) => setMinimum(sanitizeAmountTyping(e.target.value))}
              onBlur={() => setTouched(true)}
              aria-invalid={touched && !!errors.minimum}
              placeholder="0"
              className="tabular mt-1 w-full rounded-field border border-border bg-surface px-3 py-2 text-ink outline-none"
            />
            <FieldError show={touched} message={errors.minimum} />
          </div>
          <div>
            <label htmlFor="offer-max" className="text-sm font-medium text-ink">
              Largest trade
            </label>
            <input
              id="offer-max"
              inputMode="decimal"
              value={maximum}
              onChange={(e) => setMaximum(sanitizeAmountTyping(e.target.value))}
              onBlur={() => setTouched(true)}
              aria-invalid={touched && !!errors.maximum}
              placeholder="0"
              className="tabular mt-1 w-full rounded-field border border-border bg-surface px-3 py-2 text-ink outline-none"
            />
            <FieldError show={touched} message={errors.maximum} />
          </div>
        </div>
      </div>

      {/* THE CONSEQUENTIAL FIELD. */}
      <div className="space-y-3 rounded-card border border-border bg-surface p-4 shadow-card">
        <div>
          <h2 className="text-sm font-medium text-ink">How you will be paid</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            This decides how your trades are settled, so it is worth reading before you tick.
          </p>
        </div>

        {loading ? (
          <LoadingCard lines={3} />
        ) : (
          <div className="space-y-2">
            {catalogue.map((m) => (
              <label
                key={m.code}
                className="flex cursor-pointer items-center gap-3 rounded-field border border-border p-2.5 transition hover:bg-surface-sunken"
              >
                <input
                  type="checkbox"
                  checked={methods.includes(m.code)}
                  onChange={() => toggle(m.code)}
                  className="h-4 w-4 shrink-0 accent-primary"
                />
                <span className="flex-1 text-sm text-ink">{m.label}</span>
                <span
                  className={`rounded-pill px-2 py-0.5 text-micro font-medium ${
                    m.settles_automatically
                      ? 'bg-success/10 text-success'
                      : 'bg-surface-sunken text-ink-muted'
                  }`}
                >
                  {m.settles_automatically ? 'Automatic' : 'You confirm'}
                </span>
              </label>
            ))}
          </div>
        )}

        <FieldError show={touched} message={errors.methods} />

        {/* The live consequence, naming the method responsible. */}
        {consequence && (
          <div
            className={`rounded-card border p-3 text-sm ${
              consequence.automatic
                ? 'border-success/30 bg-success/5'
                : 'border-border bg-surface-sunken'
            }`}
          >
            <p className="font-medium text-ink">
              {consequence.automatic ? 'Settles automatically' : 'You confirm each payment'}
            </p>
            <p className="mt-1 text-ink-muted">{consequence.text}</p>
          </div>
        )}

        {/*
          Said when NOTHING can earn the fast path, so the seller does not hunt for a combination
          that does not exist. It is a fact about the platform today, not about their choices.
        */}
        {data && !data.automatic_settlement_available && (
          <p className="text-xs text-ink-subtle">
            No payment method settles automatically yet. That needs a payment rail we operate, and
            we do not have one.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="offer-terms" className="text-sm font-medium text-ink">
          Anything the buyer should know <span className="text-ink-subtle">(optional)</span>
        </label>
        <textarea
          id="offer-terms"
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
          rows={2}
          maxLength={280}
          placeholder="Please pay within 30 minutes."
          className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2 text-sm text-ink outline-none"
        />
      </div>

      {refusal && (
        <div className="rounded-card border border-warning/30 bg-warning/5 p-3 text-sm text-ink">
          {refusal}
        </div>
      )}

      {/*
        NOT disabled on an incomplete form: a disabled button explains nothing, and a seller staring
        at one has no way to learn which of five fields is wrong. Pressing it marks the form touched
        and every message appears at once, beside the field it belongs to. It IS disabled while a
        request is in flight, which is the one case where a second press does something harmful.
      */}
      <button
        disabled={pending}
        onClick={post}
        className="w-full rounded-field bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-40"
      >
        {pending ? 'Posting...' : 'Post offer'}
      </button>

      {touched && !complete ? (
        <p role="alert" className="text-center text-xs text-danger">
          Check the highlighted fields above.
        </p>
      ) : null}
    </div>
  );
}

/** One message, in one place, beside the field it is about. */
function FieldError({ show, message }: { show: boolean; message?: string }) {
  if (!show || !message) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-danger">
      {message}
    </p>
  );
}
